import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mediaGroupsAPI, mediaAPI, getAuthenticatedMediaUrl } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiFolder, FiUpload, FiFile, FiVideo, FiLink, FiCopy, FiEye, FiDownload, FiPlay } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const Content = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [mediaGroups, setMediaGroups] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  
  // UI states
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditMediaModal, setShowEditMediaModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);
  const [publicLinkData, setPublicLinkData] = useState(null);
  const [loadingPublicLink, setLoadingPublicLink] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null, type: null });
  
  // Form states
  const [groupFormData, setGroupFormData] = useState({
    mediaGroupName: '',
    description: ''
  });
  const [uploadFormData, setUploadFormData] = useState({
    mediaFile: null,
    mediaName: '',
    mediaGroupID: '',
    duration: 5 // Default 5 seconds for images
  });
  const [editMediaFormData, setEditMediaFormData] = useState({
    mediaName: '',
    mediaGroupID: '',
    isActive: true,
    duration: 5
  });

  const fetchData = useCallback(async () => {
    if (!user?.compID) return;
    
    try {
      const [mediaGroupsData, mediaData] = await Promise.all([
        mediaGroupsAPI.getMediaGroups(user.compID),
        mediaAPI.getAllMedia(user.compID)
      ]);
      
      setMediaGroups(mediaGroupsData);
      setAllMedia(mediaData);
      
      // Don't auto-select any group - let user choose
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.compID, selectedGroup]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMediaInGroup = () => {
    if (!selectedGroup) return allMedia.filter(media => !media.mediaGroupID);
    return allMedia.filter(media => media.mediaGroupID === selectedGroup.mediaGroupID);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getMediaIcon = (mediaType) => {
    if (mediaType.startsWith('image/')) return <FiImage className="h-8 w-8 text-blue-500" />;
    if (mediaType.startsWith('video/')) return <FiVideo className="h-8 w-8 text-purple-500" />;
    return <FiFile className="h-8 w-8 text-gray-500" />;
  };

  const handleDownloadMedia = async (media) => {
    try {
      const downloadUrl = getAuthenticatedMediaUrl(media.mediaUrl);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = media.mediaName || `media_${media.mediaID}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading media:', error);
      toast.error('Failed to download media file');
    }
  };

  const getMediaPreview = (media) => {
    const mediaUrl = getAuthenticatedMediaUrl(media.mediaUrl);
    
    // Debug logging
    console.log('Media preview debug:', {
      mediaName: media.mediaName,
      mediaUrl: media.mediaUrl,
      mediaType: media.mediaType,
      finalUrl: mediaUrl
    });
    
    // Check for image types - handle both 'image/jpeg' and 'image' formats
    if (media.mediaType === 'image' || media.mediaType.startsWith('image/')) {
      return (
        <div className="relative group">
          <img
            src={mediaUrl}
            alt={media.mediaName}
            className="w-full h-32 object-cover rounded-lg"
            onError={(e) => {
              console.error('Image failed to load:', mediaUrl);
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden absolute inset-0 bg-gray-200 rounded-lg flex items-center justify-center">
            <FiImage className="h-8 w-8 text-gray-400" />
          </div>
          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <FiEye className="h-6 w-6 text-white" />
          </div>
        </div>
      );
    } else if (media.mediaType === 'video' || media.mediaType.startsWith('video/')) {
      return (
        <div className="relative group">
          <video
            src={mediaUrl}
            className="w-full h-32 object-cover rounded-lg"
            onError={(e) => {
              console.error('Video failed to load:', mediaUrl);
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden absolute inset-0 bg-gray-200 rounded-lg flex items-center justify-center">
            <FiVideo className="h-8 w-8 text-gray-400" />
          </div>
          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <FiPlay className="h-8 w-8 text-white" />
          </div>
          <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
            VIDEO
          </div>
        </div>
      );
    } else {
      return (
        <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-center">
            {getMediaIcon(media.mediaType)}
            <p className="text-xs text-gray-500 mt-2">File Preview</p>
          </div>
        </div>
      );
    }
  };

  // Group management
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await mediaGroupsAPI.createMediaGroup(user.compID, groupFormData);
      fetchData();
      resetGroupForm();
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    try {
      await mediaGroupsAPI.updateMediaGroup(user.compID, editingGroup.mediaGroupID, groupFormData);
      fetchData();
      resetGroupForm();
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  const handleDeleteGroup = (groupId) => {
    setConfirmDialog({ isOpen: true, id: groupId, type: 'group' });
  };

  const resetGroupForm = () => {
    setGroupFormData({ mediaGroupName: '', description: '' });
    setEditingGroup(null);
    setShowCreateGroupModal(false);
  };

  // Media management
  const handleUploadMedia = async (e) => {
    e.preventDefault();
    if (!uploadFormData.mediaFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('mediaFile', uploadFormData.mediaFile);
      if (uploadFormData.mediaName) {
        formData.append('mediaName', uploadFormData.mediaName);
      }
      
      // Add duration
      if (uploadFormData.mediaFile.type.startsWith('image/')) {
        formData.append('duration', uploadFormData.duration || 5);
      } else if (uploadFormData.mediaFile.type.startsWith('video/')) {
        // Extract actual video duration from the file
        try {
          const videoDuration = await new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
              const dur = Math.ceil(video.duration);
              URL.revokeObjectURL(video.src);
              resolve(dur);
            };
            video.onerror = () => {
              URL.revokeObjectURL(video.src);
              resolve(null);
            };
            video.src = URL.createObjectURL(uploadFormData.mediaFile);
          });
          if (videoDuration) {
            formData.append('duration', videoDuration);
          }
        } catch (err) {
          // If extraction fails, backend will store null — device uses actual element.duration
        }
      }

      const groupID = selectedGroup?.mediaGroupID || uploadFormData.mediaGroupID;
      await mediaAPI.uploadMedia(user.compID, groupID, formData);
      
      fetchData();
      resetUploadForm();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Error uploading media. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateMedia = async (e) => {
    e.preventDefault();
    try {
      await mediaAPI.updateMedia(user.compID, editingMedia.mediaID, editMediaFormData);
      setShowEditMediaModal(false);
      setEditingMedia(null);
      fetchData();
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Error updating media. Please try again.');
    }
  };

  const handleDeleteMedia = (mediaId) => {
    setConfirmDialog({ isOpen: true, id: mediaId, type: 'media' });
  };

  const confirmDelete = async () => {
    const { id, type } = confirmDialog;
    try {
      if (type === 'group') {
        await mediaGroupsAPI.deleteMediaGroup(user.compID, id);
        if (selectedGroup && selectedGroup.mediaGroupID === id) {
          setSelectedGroup(null);
        }
        toast.success('Media group deleted successfully!');
      } else if (type === 'media') {
        await mediaAPI.deleteMedia(user.compID, id);
        toast.success('Media deleted successfully!');
      }
      fetchData();
      setConfirmDialog({ isOpen: false, id: null, type: null });
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      toast.error(`Error deleting ${type}. Please try again.`);
      setConfirmDialog({ isOpen: false, id: null, type: null });
    }
  };

  const resetUploadForm = () => {
    setUploadFormData({
      mediaFile: null,
      mediaName: '',
      mediaGroupID: '',
      duration: 5
    });
    setShowUploadModal(false);
  };

  const startEditGroup = (group) => {
    setEditingGroup(group);
    setGroupFormData({
      mediaGroupName: group.mediaGroupName,
      description: group.description || ''
    });
    setShowCreateGroupModal(true);
  };

  const startEditMedia = (media) => {
    setEditingMedia(media);
    setEditMediaFormData({
      mediaName: media.mediaName,
      mediaGroupID: media.mediaGroupID || '',
      isActive: media.isActive,
      duration: media.duration || 5
    });
    setShowEditMediaModal(true);
  };

  const handleGeneratePublicLink = async (group) => {
    try {
      setLoadingPublicLink(true);
      setError('');
      
      const data = await mediaGroupsAPI.generatePublicLink(user.compID, group.mediaGroupID);
      setPublicLinkData({
        ...data,
        mediaGroupName: group.mediaGroupName
      });
      setShowPublicLinkModal(true);
    } catch (error) {
      console.error('Error generating public link:', error);
      setError(error.response?.data?.error || 'Failed to generate public link. Please try again.');
    } finally {
      setLoadingPublicLink(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const closePublicLinkModal = () => {
    setShowPublicLinkModal(false);
    setPublicLinkData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  // Empty state component
  const EmptyState = ({ title, description, icon: Icon, actionText, onAction }) => (
    <div className="text-center py-12">
      <Icon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {onAction && (
        <div className="mt-6">
          <button
            onClick={onAction}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <FiPlus className="-ml-1 mr-2 h-4 w-4" />
            {actionText}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col p-6">
      {/* Header */}
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
        <p className="mt-2 text-gray-600">
          Organize and manage your digital signage content
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4 flex-shrink-0">
          <div className="flex items-center">
            <div className="text-red-400">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-6 min-h-0">
        {/* Left Panel - Media Groups */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiFolder className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Media Groups</h2>
              </div>
              <button
                onClick={() => setShowCreateGroupModal(true)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Add Group"
              >
                <FiPlus className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Ungrouped media option */}
            <div
              onClick={() => setSelectedGroup(null)}
              className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                !selectedGroup ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Ungrouped Media</h3>
                  <p className="text-sm text-gray-500">Media files not assigned to any group</p>
                </div>
                <span className="text-sm text-gray-500">
                  {allMedia.filter(m => !m.mediaGroupID).length}
                </span>
              </div>
            </div>

            {mediaGroups.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  title="No Media Groups"
                  description="Create your first media group to organize your content."
                  icon={FiFolder}
                  actionText="Create Group"
                  onAction={() => setShowCreateGroupModal(true)}
                />
              </div>
            ) : (
              mediaGroups.map(group => {
                const groupMedia = allMedia.filter(media => media.mediaGroupID === group.mediaGroupID);
                
                return (
                  <div
                    key={group.mediaGroupID}
                    onClick={() => setSelectedGroup(group)}
                    className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                      selectedGroup?.mediaGroupID === group.mediaGroupID 
                        ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                        : 'hover:bg-gray-50'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{group.mediaGroupName}</h3>
                      <p className="text-sm text-gray-500">{group.description}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-500">
                          {groupMedia.length} files
                        </span>
                        <span className="text-xs text-blue-600">
                          {formatFileSize(groupMedia.reduce((sum, m) => sum + (m.fileSize || 0), 0))}
                        </span>
                      </div>
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGeneratePublicLink(group);
                          }}
                          disabled={loadingPublicLink}
                          className="flex items-center space-x-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Get public link for this media group (existing link will be reused if available)"
                        >
                          {loadingPublicLink ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-green-600"></div>
                              <span>Loading...</span>
                            </>
                          ) : (
                            <>
                              <FiLink className="h-3 w-3" />
                              <span>Get Public Link</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditGroup(group);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <FiEdit2 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGroup(group.mediaGroupID);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }))}
          </div>
        </div>

        {/* Right Panel - Media in Selected Group */}
        <div className="lg:col-span-3 bg-white rounded-lg shadow flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiImage className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedGroup ? selectedGroup.mediaGroupName : 'Ungrouped Media'}
                </h2>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
              >
                <FiUpload className="h-3 w-3" />
                <span>Upload Media</span>
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto min-h-0 p-4">
            {getMediaInGroup().length === 0 ? (
              <EmptyState
                title="No Media Files"
                description={selectedGroup 
                  ? 'No media files in this group yet.' 
                  : 'No ungrouped media files found.'
                }
                icon={FiImage}
                actionText="Upload First File" 
                onAction={() => setShowUploadModal(true)}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getMediaInGroup().map(media => (
                  <div key={media.mediaID} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-white">
                    {/* Media Preview */}
                    <div className="relative">
                      {getMediaPreview(media)}
                    </div>
                    
                    {/* Media Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 truncate flex-1 mr-2">
                          {media.mediaName}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          media.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {media.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-500 capitalize mb-2">{media.mediaType}</p>
                      
                      <div className="space-y-1 text-xs text-gray-500 mb-3">
                        <div>Size: {formatFileSize(media.fileSize)}</div>
                        {media.duration && (
                          <div>Duration: {media.duration}s</div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => handleDownloadMedia(media)}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                          title="Download file"
                        >
                          <FiDownload className="h-3 w-3" />
                          <span>Download</span>
                        </button>
                        
                        <div className="flex space-x-1">
                          <button
                            onClick={() => startEditMedia(media)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-gray-100"
                            title="Edit media"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteMedia(media.mediaID)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-gray-100"
                            title="Delete media"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Group Create/Edit Modal */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingGroup ? 'Edit Media Group' : 'Create Media Group'}
            </h3>
            <form onSubmit={editingGroup ? handleUpdateGroup : handleCreateGroup}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={groupFormData.mediaGroupName}
                    onChange={(e) => setGroupFormData({...groupFormData, mediaGroupName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={groupFormData.description}
                    onChange={(e) => setGroupFormData({...groupFormData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={resetGroupForm}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingGroup ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Upload Media</h3>
            <form onSubmit={handleUploadMedia}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media File
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFormData({...uploadFormData, mediaFile: e.target.files[0]})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    accept="image/*,video/*"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={uploadFormData.mediaName}
                    onChange={(e) => setUploadFormData({...uploadFormData, mediaName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty to use filename"
                  />
                </div>
                {uploadFormData.mediaFile && uploadFormData.mediaFile.type.startsWith('image/') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Duration (seconds)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={uploadFormData.duration}
                        onChange={(e) => setUploadFormData({...uploadFormData, duration: parseInt(e.target.value) || 5})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="5"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">sec</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      How long to display this image (1-300 seconds)
                    </p>
                  </div>
                )}
                {!selectedGroup && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Media Group
                    </label>
                    <select
                      value={uploadFormData.mediaGroupID}
                      onChange={(e) => setUploadFormData({...uploadFormData, mediaGroupID: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">No Group (Ungrouped)</option>
                      {mediaGroups.map(group => (
                        <option key={group.mediaGroupID} value={group.mediaGroupID}>
                          {group.mediaGroupName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={resetUploadForm}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Edit Modal */}
      {showEditMediaModal && editingMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Media</h3>
            <form onSubmit={handleUpdateMedia}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media Name
                  </label>
                  <input
                    type="text"
                    value={editMediaFormData.mediaName}
                    onChange={(e) => setEditMediaFormData({...editMediaFormData, mediaName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media Group
                  </label>
                  <select
                    value={editMediaFormData.mediaGroupID}
                    onChange={(e) => setEditMediaFormData({...editMediaFormData, mediaGroupID: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No Group (Ungrouped)</option>
                    {mediaGroups.map(group => (
                      <option key={group.mediaGroupID} value={group.mediaGroupID}>
                        {group.mediaGroupName}
                      </option>
                    ))}
                  </select>
                </div>
                {editingMedia && editingMedia.mediaType && editingMedia.mediaType.startsWith('image/') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Duration (seconds)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={editMediaFormData.duration}
                        onChange={(e) => setEditMediaFormData({...editMediaFormData, duration: parseInt(e.target.value) || 5})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="5"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">sec</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      How long to display this image (1-300 seconds)
                    </p>
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editMediaFormData.isActive}
                    onChange={(e) => setEditMediaFormData({...editMediaFormData, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditMediaModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Public Link Modal */}
      {showPublicLinkModal && publicLinkData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Public Link Ready
                </h2>
                <button
                  onClick={closePublicLinkModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Public link for <strong>{publicLinkData.mediaGroupName}</strong>:
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-3 border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono text-gray-700 break-all">
                        {publicLinkData.publicUrl}
                      </span>
                      <button
                        onClick={() => copyToClipboard(publicLinkData.publicUrl)}
                        className="ml-2 p-1 text-gray-500 hover:text-blue-600 flex-shrink-0"
                        title="Copy to clipboard"
                      >
                        <FiCopy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-blue-400 mt-0.5">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-900">
                        Share this link
                      </h3>
                      <p className="text-sm text-blue-700 mt-1">
                        Anyone with this link can view the media group content in fullscreen mode. 
                        The link is public and doesn't require authentication. This same link will be provided each time you click "Get Public Link" - no need to update your displays.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => window.open(publicLinkData.publicUrl, '_blank')}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <FiEye className="h-4 w-4" />
                    <span>Preview</span>
                  </button>
                  <button
                    onClick={closePublicLinkModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null, type: null })}
        onConfirm={confirmDelete}
        title={confirmDialog.type === 'group' ? 'Delete Media Group' : 'Delete Media'}
        message={
          confirmDialog.type === 'group'
            ? 'Are you sure you want to delete this media group? This action cannot be undone.'
            : 'Are you sure you want to permanently delete this media file? This action cannot be undone and the file will be removed from all devices.'
        }
      />
    </div>
  );
};

export default Content;
